import {getCurrentUserLogin} from '@github-ui/client-env'
import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import type {PinCommentMutationMutation} from '@github-ui/server-persisted-queries/types/pinCommentMutation'
import type {UnpinCommentMutationMutation} from '@github-ui/server-persisted-queries/types/unpinCommentMutation'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate} from 'react-relay/hooks'
import type {Disposable, Environment, RecordProxy, RecordSourceProxy} from 'relay-runtime'

const PIN_ERROR = 'Failed to pin comment'
const UNPIN_ERROR = 'Failed to unpin comment'
const SHORT_BODY_STORAGE_KEY = 'shortBodyHTML(limit:700,stripBlockElements:false)'

type PinIssueComment = NonNullable<NonNullable<PinCommentMutationMutation['pinIssueComment']>['issueComment']>
type UnpinIssueComment = NonNullable<NonNullable<UnpinCommentMutationMutation['unpinIssueComment']>['issueComment']>
type PinnedCommentCard = NonNullable<PinIssueComment['issue']['pinnedIssueComment']>
type PinnedComment = PinnedCommentCard['issueComment']
type CommentActor = NonNullable<PinnedCommentCard['pinnedBy'] | PinnedComment['author']>
type OptimisticPinnedCommentCard = Omit<PinnedCommentCard, 'pinnedBy'> & {
  readonly pinnedBy: PinnedCommentCard['pinnedBy'] | null
}

export type PinnedCommentMutationOptions = {
  issueId: PinIssueComment['issue']['id'] | null
  nameWithOwner: string
  comment: Pick<PinnedComment, 'id' | 'shortBodyHTML' | 'createdAt' | 'url' | 'author'>
}

type PinningResult =
  | {
      readonly pinned: true
      readonly issueId: PinIssueComment['issue']['id']
      readonly commentId: PinIssueComment['id']
      readonly pinnedIssueComment: PinnedCommentCard
    }
  | {
      readonly pinned: false
      readonly issueId: UnpinIssueComment['issue']['id']
      readonly commentId: UnpinIssueComment['id']
      readonly pinnedIssueComment: null
    }

type OptimisticPinningResult =
  | (Omit<Extract<PinningResult, {pinned: true}>, 'pinnedIssueComment'> & {
      readonly pinnedIssueComment: OptimisticPinnedCommentCard
    })
  | Extract<PinningResult, {pinned: false}>

type GraphQLResponse<TData> = {
  readonly data?: TData | null
  readonly errors?: ReadonlyArray<{readonly message?: string}> | null
}

type CommentPinningPublication = {
  completed: (fields: Record<string, unknown>) => void
  failed: (error: Error) => void
}

type RelayPinningContext = {
  relayUpdate: Disposable
  commentPublication: CommentPinningPublication
}

async function readGraphQLResponse<TData>(response: Response, fallbackMessage: string): Promise<TData> {
  let result: GraphQLResponse<TData> | null
  try {
    result = (await response.json()) as GraphQLResponse<TData> | null
  } catch (error) {
    throw new Error(response.ok ? fallbackMessage : `${fallbackMessage} (${response.status})`, {cause: error})
  }

  const serverMessage = result?.errors?.[0]?.message
  if (!response.ok) throw new Error(serverMessage ?? `${fallbackMessage} (${response.status})`)
  if (result?.errors?.length) throw new Error(serverMessage ?? fallbackMessage)
  if (!result?.data) throw new Error(fallbackMessage)

  return result.data
}

async function submitCommentPinningMutation({
  pinned,
  issueId,
  nameWithOwner,
  commentId,
}: {
  pinned: boolean
  issueId: string
  nameWithOwner: string
  commentId: string
}): Promise<PinningResult> {
  const fallbackMessage = pinned ? PIN_ERROR : UNPIN_ERROR
  let response: Response
  try {
    response = await reactFetchJSON(`/${nameWithOwner}/comments/pin`, {
      method: pinned ? 'PUT' : 'DELETE',
      body: {input: {issueCommentId: commentId}},
    })
  } catch (error) {
    throw new Error(fallbackMessage, {cause: error})
  }

  if (pinned) {
    const data = await readGraphQLResponse<PinCommentMutationMutation>(response, fallbackMessage)
    const issueComment = data.pinIssueComment?.issueComment
    const issue = issueComment?.issue
    const pinnedIssueComment = issue?.pinnedIssueComment

    if (
      !issueComment ||
      issueComment.id !== commentId ||
      issueComment.isPinned !== true ||
      issue?.id !== issueId ||
      !pinnedIssueComment ||
      !pinnedIssueComment.pinnedBy ||
      !pinnedIssueComment.issueComment ||
      pinnedIssueComment.issueComment.id !== commentId
    ) {
      throw new Error(fallbackMessage)
    }

    return {
      pinned: true,
      issueId: issue.id,
      commentId: issueComment.id,
      pinnedIssueComment,
    }
  }

  const data = await readGraphQLResponse<UnpinCommentMutationMutation>(response, fallbackMessage)
  const issueComment = data.unpinIssueComment?.issueComment
  const issue = issueComment?.issue
  if (
    !issueComment ||
    issueComment.id !== commentId ||
    issueComment.isPinned !== false ||
    issue?.id !== issueId ||
    issue.pinnedIssueComment !== null
  ) {
    throw new Error(fallbackMessage)
  }

  return {
    pinned: false,
    issueId: issue.id,
    commentId: issueComment.id,
    pinnedIssueComment: null,
  }
}

function writeActor(store: RecordSourceProxy, actor: CommentActor): RecordProxy {
  const record = store.get(actor.id) ?? store.create(actor.id, actor.__typename)
  record.setValue(actor.id, 'id')
  record.setValue(actor.login, 'login')
  if ('avatarUrl' in actor) {
    record.setValue(actor.avatarUrl, 'avatarUrl')
    record.setValue(actor.profileUrl, 'profileUrl')
  }
  return record
}

function writeOptimisticPin(
  store: RecordSourceProxy,
  issueId: string,
  commentId: string,
  pinnedIssueComment: OptimisticPinnedCommentCard,
): void {
  const issueRecord = store.get(issueId)
  const commentRecord = store.get(commentId)
  if (!issueRecord || !commentRecord) return

  commentRecord.setValue(true, 'isPinned')
  commentRecord.setValue(true, 'viewerCanUnpin')
  commentRecord.setValue(false, 'viewerCanPin')

  const pinnedRecord = store.get(pinnedIssueComment.id) ?? store.create(pinnedIssueComment.id, 'PinnedIssueComment')
  pinnedRecord.setValue(pinnedIssueComment.id, 'id')
  pinnedRecord.setLinkedRecord(commentRecord, 'issueComment')

  if (pinnedIssueComment.pinnedBy) {
    const pinnedByRecord = writeActor(store, pinnedIssueComment.pinnedBy)
    pinnedRecord.setLinkedRecord(pinnedByRecord, 'pinnedBy')
    commentRecord.setLinkedRecord(pinnedByRecord, 'pinnedBy')
  }

  if (commentRecord.getValue(SHORT_BODY_STORAGE_KEY) == null) {
    commentRecord.setValue(pinnedIssueComment.issueComment.shortBodyHTML, SHORT_BODY_STORAGE_KEY)
  }

  issueRecord.setLinkedRecord(pinnedRecord, 'pinnedIssueComment')
}

function writeUnpinnedState(store: RecordSourceProxy, issueId: string, commentId: string): void {
  const commentRecord = store.get(commentId)
  commentRecord?.setValue(false, 'isPinned')
  commentRecord?.setValue(true, 'viewerCanPin')
  commentRecord?.setValue(false, 'viewerCanUnpin')
  store.get(commentId)?.setValue(null, 'pinnedBy')
  store.get(issueId)?.setValue(null, 'pinnedIssueComment')
}

function applyOptimisticRelayUpdate(environment: Environment, result: OptimisticPinningResult): Disposable {
  return environment.applyUpdate({
    storeUpdater: store => {
      if (result.pinned) {
        writeOptimisticPin(store, result.issueId, result.commentId, result.pinnedIssueComment)
      } else {
        writeUnpinnedState(store, result.issueId, result.commentId)
      }
    },
  })
}

function writeConfirmedPin(store: RecordSourceProxy, result: Extract<PinningResult, {pinned: true}>): void {
  const {pinnedIssueComment} = result
  const comment = pinnedIssueComment.issueComment
  const commentRecord = store.get(result.commentId) ?? store.create(result.commentId, 'IssueComment')
  commentRecord.setValue(result.commentId, 'id')
  commentRecord.setValue(true, 'isPinned')
  commentRecord.setValue(comment.viewerCanUnpin, 'viewerCanUnpin')
  commentRecord.setValue(false, 'viewerCanPin')
  commentRecord.setValue(comment.shortBodyHTML, SHORT_BODY_STORAGE_KEY)
  commentRecord.setValue(comment.createdAt, 'createdAt')
  commentRecord.setValue(comment.url, 'url')

  if (comment.author) {
    commentRecord.setLinkedRecord(writeActor(store, comment.author), 'author')
  } else {
    commentRecord.setValue(null, 'author')
  }

  const pinnedByRecord = pinnedIssueComment.pinnedBy ? writeActor(store, pinnedIssueComment.pinnedBy) : null
  commentRecord.setLinkedRecord(pinnedByRecord, 'pinnedBy')

  const pinnedRecord = store.get(pinnedIssueComment.id) ?? store.create(pinnedIssueComment.id, 'PinnedIssueComment')
  pinnedRecord.setValue(pinnedIssueComment.id, 'id')
  pinnedRecord.setLinkedRecord(commentRecord, 'issueComment')
  pinnedRecord.setLinkedRecord(pinnedByRecord, 'pinnedBy')
  store.get(result.issueId)?.setLinkedRecord(pinnedRecord, 'pinnedIssueComment')
}

function settleRelayUpdate(environment: Environment, result: PinningResult, relayUpdate: Disposable): void {
  commitLocalUpdate(environment, store => {
    if (result.pinned) {
      writeConfirmedPin(store, result)
    } else {
      writeUnpinnedState(store, result.issueId, result.commentId)
    }
  })
  // The confirmed values must reach Relay's base records before the optimistic layer is removed.
  relayUpdate.dispose()
}

function commentPinningFields(result: PinningResult): Record<string, unknown> {
  if (result.pinned) {
    return {
      isPinned: true,
      viewerCanPin: false,
      viewerCanUnpin: result.pinnedIssueComment.issueComment.viewerCanUnpin,
      pinnedBy: result.pinnedIssueComment.pinnedBy,
    }
  }

  return {isPinned: false, viewerCanPin: true, viewerCanUnpin: false, pinnedBy: null}
}

function optimisticPinnedComment(
  issueId: string,
  comment: PinnedCommentMutationOptions['comment'],
): OptimisticPinnedCommentCard {
  const viewerLogin = getCurrentUserLogin()
  return {
    id: `client:pinnedIssueComment:${issueId}`,
    pinnedBy: viewerLogin
      ? {
          __typename: 'User',
          id: `client:pinnedBy:${comment.id}`,
          login: viewerLogin,
        }
      : null,
    issueComment: {...comment, viewerCanUnpin: true},
  }
}

function useCommentPinningMutation(
  pinned: boolean,
  options: PinnedCommentMutationOptions,
  onError: () => void,
  environment: Environment,
) {
  const issueId = options.issueId
  const {mutate} = useEntityMutation<PinningResult, Error, RelayPinningContext>({
    scope: {id: `comment-pinning:${issueId ?? options.comment.id}`},
    mutationFn: () => {
      if (!issueId) throw new Error(pinned ? PIN_ERROR : UNPIN_ERROR)
      return submitCommentPinningMutation({
        pinned,
        issueId,
        nameWithOwner: options.nameWithOwner,
        commentId: options.comment.id,
      })
    },
    getConfirmedOperation: result => ({
      type: 'setFields',
      id: result.issueId,
      fields: {pinnedIssueComment: result.pinnedIssueComment},
    }),
    onMutate: ({operation}) => {
      if (!issueId) throw new Error(pinned ? PIN_ERROR : UNPIN_ERROR)
      const pinnedIssueComment = operation.fields.pinnedIssueComment as OptimisticPinnedCommentCard | null
      const optimisticResult: OptimisticPinningResult = pinned
        ? {
            pinned: true,
            issueId,
            commentId: options.comment.id,
            pinnedIssueComment: pinnedIssueComment as OptimisticPinnedCommentCard,
          }
        : {pinned: false, issueId, commentId: options.comment.id, pinnedIssueComment: null}
      const commentPublication = publishOptimisticFields(options.comment.id, {
        isPinned: pinned,
        viewerCanPin: !pinned,
        viewerCanUnpin: pinned,
        pinnedBy: pinned ? pinnedIssueComment?.pinnedBy : null,
      })
      return {
        relayUpdate: applyOptimisticRelayUpdate(environment, optimisticResult),
        commentPublication,
      }
    },
    onSuccess: (result, _variables, context) => {
      if (context) {
        context.commentPublication.completed(commentPinningFields(result))
        settleRelayUpdate(environment, result, context.relayUpdate)
      }
    },
    onError: (error, _variables, context) => {
      context?.relayUpdate.dispose()
      context?.commentPublication.failed(error)
      onError()
    },
  })

  return useCallback(() => {
    if (!issueId) return
    const operation: SetFieldsOperation = {
      type: 'setFields',
      id: issueId,
      fields: {
        pinnedIssueComment: pinned ? optimisticPinnedComment(issueId, options.comment) : null,
      },
    }
    mutate({operation})
  }, [issueId, mutate, options.comment, pinned])
}

export function usePinnedComment(options: PinnedCommentMutationOptions) {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const handlePinError = useCallback(() => {
    // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
    addToast({type: 'error', message: PIN_ERROR})
  }, [addToast])
  const handleUnpinError = useCallback(() => {
    // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
    addToast({type: 'error', message: UNPIN_ERROR})
  }, [addToast])

  const pinComment = useCommentPinningMutation(true, options, handlePinError, environment)
  const unpinComment = useCommentPinningMutation(false, options, handleUnpinError, environment)

  return {pinComment, unpinComment}
}
